import { type FC, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from '@/components/ui';
import { useAuth, useEventRegistrationSettings } from '@/hooks';
import { useWaiverSettings } from '@/hooks/config/useWaiverSettings';
import { supabase } from '@/lib/api/supabase';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';
import {
  Trophy,
  User,
  LogOut,
  CalendarDays,
  CalendarPlus,
  MapPin,
  ArrowRight,
  Sparkles,
  Clock,
  CheckCircle2,
  Swords,
  FileSignature,
  AlertCircle,
  Loader2,
  ExternalLink,
  X,
  ShoppingBag,
  Package
} from 'lucide-react';
import type { Tournament } from '@/types';
import { dbToTournament } from '@/types';
import { generateCactusCupTournamentInvite } from '@/lib/utils/calendar';

interface RegistrationData {
  tournaments: Array<{
    id: string;
    tournament: Tournament;
    registeredAt: string;
    paymentStatus: string;
  }>;
}

interface PurchasedAddon {
  id: string;
  itemName: string;
  quantity: number;
  variantName: string | null;
  price: number;
  purchasedAt: string;
  imageUrl: string | null;
}

/**
 * User dashboard page - Punchy redesign with animations
 */
export const DashboardPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, loading } = useAuth();
  const { settings: eventRegSettings } = useEventRegistrationSettings();

  const [registrations, setRegistrations] = useState<RegistrationData>({
    tournaments: [],
  });
  const [purchasedAddons, setPurchasedAddons] = useState<PurchasedAddon[]>([]);
  const [hasEventRegistration, setHasEventRegistration] = useState(false);
  const [ownedDayPassIds, setOwnedDayPassIds] = useState<string[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [eventDate, setEventDate] = useState('June 14-16, 2025');
  const [eventVenue, setEventVenue] = useState('Phoenix, Arizona');

  // Waiver state
  const {
    settings: waiverSettings,
    waiverStatus,
    isLoading: waiverLoading,
    createWaiver,
    refreshStatus: refreshWaiverStatus,
    error: waiverError,
  } = useWaiverSettings(2026);

  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [isCreatingWaiver, setIsCreatingWaiver] = useState(false);
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [localWaiverError, setLocalWaiverError] = useState<string | null>(null);

  // Fetch event settings from about page (site_settings)
  useEffect(() => {
    const fetchEventSettings = async (): Promise<void> => {
      try {
        const { data } = await supabase
          .from('site_settings')
          .select('setting_key, setting_value')
          .in('setting_key', ['event_date', 'event_venue']);

        if (data) {
          for (const row of data) {
            if (row.setting_key === 'event_date' && row.setting_value) {
              setEventDate(row.setting_value);
            }
            if (row.setting_key === 'event_venue' && row.setting_value) {
              setEventVenue(row.setting_value);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch event settings:', err);
      }
    };

    void fetchEventSettings();
  }, []);

  // Show success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message as string);
      window.history.replaceState({}, document.title);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location]);

  // Fetch user registrations and purchased addons
  useEffect(() => {
    const fetchRegistrations = async (): Promise<void> => {
      if (!user) return;

      try {
        setLoadingData(true);

        // Fetch tournament registrations
        const { data: tournamentRegs, error: tourError } = await supabase
          .from('tournament_registrations')
          .select(`
            id,
            registered_at,
            payment_status,
            tournament_id
          `)
          .eq('user_id', user.id)
          .order('registered_at', { ascending: false });

        if (tourError) throw tourError;

        const tournamentIds = tournamentRegs?.map((r) => r.tournament_id) || [];
        const { data: tournaments } = await supabase
          .from('tournaments')
          .select('*')
          .in('id', tournamentIds);

        const tournamentData = (tournamentRegs || [])
          .map((reg) => {
            const tournament = tournaments?.find((t) => t.id === reg.tournament_id);
            if (!tournament) return null;
            return {
              id: reg.id,
              tournament: dbToTournament(tournament),
              registeredAt: reg.registered_at,
              paymentStatus: reg.payment_status,
            };
          })
          .filter((r): r is NonNullable<typeof r> => r !== null);

        setRegistrations({
          tournaments: tournamentData,
        });

        // Check if user has an event registration OR spectator registration,
        // and collect any day passes they hold for display
        const [{ data: eventRegs }, { data: spectatorRegs }] = await Promise.all([
          supabase.from('event_registrations').select('id, day_pass_id').eq('user_id', user.id),
          supabase.from('spectator_registrations').select('id').eq('user_id', user.id).limit(1),
        ]);

        setHasEventRegistration((eventRegs?.length ?? 0) > 0 || (spectatorRegs?.length ?? 0) > 0);
        setOwnedDayPassIds(
          [...new Set(
            (eventRegs ?? [])
              .map((r) => r.day_pass_id)
              .filter((id): id is string => typeof id === 'string')
          )]
        );

        // Fetch purchased addons from order_items via orders
        const { data: userOrders } = await supabase
          .from('orders')
          .select('id, created_at')
          .eq('user_id', user.id)
          .eq('payment_status', 'paid');

        if (userOrders && userOrders.length > 0) {
          const orderIds = userOrders.map((o) => o.id);
          const orderCreatedAtMap = new Map(userOrders.map((o) => [o.id, o.created_at]));

          const { data: addonItems } = await supabase
            .from('order_items')
            .select(`
              id,
              item_name,
              quantity,
              variant_name,
              price,
              order_id,
              addon_id
            `)
            .in('order_id', orderIds)
            .not('addon_id', 'is', null);

          if (addonItems && addonItems.length > 0) {
            // Fetch addon details for images
            const addonIds = [...new Set(addonItems.map((item) => item.addon_id).filter(Boolean))];
            const { data: addons } = await supabase
              .from('addons')
              .select('id, image_url')
              .in('id', addonIds as string[]);

            const addonImageMap = new Map(addons?.map((a) => [a.id, a.image_url]) || []);

            const purchasedAddonData: PurchasedAddon[] = addonItems.map((item) => ({
              id: item.id,
              itemName: item.item_name,
              quantity: item.quantity || 1,
              variantName: item.variant_name,
              price: item.price,
              purchasedAt: orderCreatedAtMap.get(item.order_id) || '',
              imageUrl: item.addon_id ? addonImageMap.get(item.addon_id) || null : null,
            }));

            setPurchasedAddons(purchasedAddonData);
          }
        }
      } catch (err) {
        console.error('Failed to fetch registrations:', err);
      } finally {
        setLoadingData(false);
      }
    };

    if (user) {
      void fetchRegistrations();
    }
  }, [user]);

  // Pre-fill email from user for waiver signing
  useEffect(() => {
    if (user?.email) {
      setSignerEmail(user.email);
    }
    if (user?.user_metadata?.full_name) {
      setSignerName(user.user_metadata.full_name);
    }
  }, [user]);

  // Handle iframe message for waiver completion detection
  useEffect(() => {
    if (!showWaiverModal || !signUrl) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'boldsign-signed' || event.data?.status === 'completed') {
        setShowWaiverModal(false);
        setSignUrl(null);
        refreshWaiverStatus();
        setSuccessMessage('Waiver signed successfully!');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showWaiverModal, signUrl, refreshWaiverStatus]);

  // Poll for waiver status updates when iframe is showing
  useEffect(() => {
    if (!showWaiverModal || !signUrl) return;

    const interval = setInterval(() => {
      refreshWaiverStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [showWaiverModal, signUrl, refreshWaiverStatus]);

  // Close modal when waiver is signed
  useEffect(() => {
    if (waiverStatus?.status === 'signed' && showWaiverModal) {
      setShowWaiverModal(false);
      setSignUrl(null);
      setSuccessMessage('Waiver signed successfully!');
    }
  }, [waiverStatus?.status, showWaiverModal]);

  // Handle waiver creation
  const handleCreateWaiver = useCallback(async () => {
    if (!signerName.trim()) {
      setLocalWaiverError('Please enter your full legal name');
      return;
    }
    if (!signerEmail.trim()) {
      setLocalWaiverError('Please enter your email address');
      return;
    }

    setLocalWaiverError(null);
    setIsCreatingWaiver(true);

    try {
      const result = await createWaiver(signerName.trim(), signerEmail.trim());

      if (result?.signUrl) {
        setSignUrl(result.signUrl);
      } else {
        // Already signed or error
        await refreshWaiverStatus();
      }
    } catch {
      setLocalWaiverError('Failed to create waiver. Please try again.');
    } finally {
      setIsCreatingWaiver(false);
    }
  }, [signerName, signerEmail, createWaiver, refreshWaiverStatus]);

  // Get waiver display status
  const getWaiverDisplayStatus = () => {
    if (waiverLoading) return 'loading';
    if (!waiverStatus) return 'not_signed';
    switch (waiverStatus.status) {
      case 'signed':
        return 'signed';
      case 'pending':
      case 'sent':
      case 'viewed':
        return 'pending';
      case 'declined':
      case 'expired':
      case 'error':
        return 'needs_action';
      default:
        return 'not_signed';
    }
  };

  if (!loading && !user) {
    navigate('/login');
    return null;
  }

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (loading || loadingData) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white/80 text-lg">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const hasRegistrations = registrations.tournaments.length > 0 || hasEventRegistration;
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Warrior';
  const dayPassName = (id: string): string =>
    eventRegSettings.dayPasses.find((p) => p.id === id)?.name
      ?? `${id.charAt(0).toUpperCase()}${id.slice(1)} Pass`;

  // Waiver status display info (moved up for use in both hero sections)
  const waiverDisplayStatus = getWaiverDisplayStatus();

  // Hero CTA for users with no registrations
  const NoRegistrationsHero = () => (
    <div className="animate-on-load animate-fade-in-up">
      {/* Main Hero Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 p-8 md:p-12 shadow-2xl">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-turquoise-400/20 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-400/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4 animate-on-load animate-fade-in-down animation-delay-200">
            <Sparkles className="h-5 w-5 text-yellow-300 animate-bounce-subtle" />
            <span className="text-yellow-200 font-medium text-sm uppercase tracking-wider">
              Don't Miss Out!
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-viking text-white mb-4 animate-on-load animate-fade-in-up animation-delay-300">
            Get Ready to Attend
          </h2>

          <p className="text-white/90 text-lg md:text-xl mb-8 max-w-xl animate-on-load animate-fade-in-up animation-delay-400">
            You haven't registered for the event yet. Join your peers at the
            <span className="font-bold text-yellow-200"> Cactus Cup 2026</span> and prove your worth!
          </p>

          {/* Event Info Pills */}
          <div className="flex flex-wrap gap-3 mb-8 animate-on-load animate-fade-in-up animation-delay-500">
            <div className="flex items-center gap-2 bg-turquoise-900/50 backdrop-blur-sm rounded-full px-4 py-2">
              <CalendarDays className="h-4 w-4 text-yellow-200" />
              <span className="text-white text-sm font-medium">{eventDate}</span>
            </div>
            <div className="flex items-center gap-2 bg-turquoise-900/50 backdrop-blur-sm rounded-full px-4 py-2">
              <MapPin className="h-4 w-4 text-yellow-200" />
              <span className="text-white text-sm font-medium">{eventVenue}</span>
            </div>
          </div>

          {/* CTA Button */}
          {!REGISTRATIONS_CLOSED ? (
            <div className="animate-on-load animate-fade-in-up animation-delay-600">
              <Button
                size="lg"
                onClick={() => navigate('/event-registration')}
                className="group bg-turquoise-900 text-orange-400 hover:bg-turquoise-800 hover:text-orange-300 font-bold text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse-glow"
              >
                <Swords className="mr-2 h-5 w-5 group-hover:animate-wiggle" />
                Register Now
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          ) : (
            <ClosedNotice className="text-center" />
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="flex flex-wrap justify-center gap-3 mt-8 animate-on-load animate-fade-in-up animation-delay-700">
        <Button
          variant="outline"
          onClick={() => navigate('/events')}
          className="border-turquoise-600 text-turquoise-200 hover:bg-turquoise-800 hover:text-white group"
        >
          <Trophy className="mr-2 h-4 w-4 text-orange-400" />
          Browse Events
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
        {/* Waiver Button - only show if user has an event registration */}
        {hasEventRegistration && waiverDisplayStatus !== 'signed' && waiverDisplayStatus !== 'loading' && (
          <Button
            variant="outline"
            onClick={() => setShowWaiverModal(true)}
            className={`group ${
              waiverDisplayStatus === 'pending'
                ? 'border-yellow-500 text-yellow-200 hover:bg-yellow-500/20 hover:text-yellow-100'
                : 'border-orange-500 text-orange-200 hover:bg-orange-500/20 hover:text-orange-100'
            }`}
          >
            <FileSignature className="mr-2 h-4 w-4" />
            {waiverDisplayStatus === 'pending' ? 'Complete Waiver' : 'Sign Waiver'}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => navigate('/profile')}
          className="border-turquoise-600 text-turquoise-200 hover:bg-turquoise-800 hover:text-white group"
        >
          <User className="mr-2 h-4 w-4 text-orange-400" />
          Edit Profile
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/orders')}
          className="border-turquoise-600 text-turquoise-200 hover:bg-turquoise-800 hover:text-white group"
        >
          <Clock className="mr-2 h-4 w-4 text-orange-400" />
          Order History
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );

  // Waiver Status Card Component
  const WaiverStatusCard = () => {
    if (waiverDisplayStatus === 'loading') {
      return (
        <Card className="hover-lift animate-on-load animate-fade-in-up animation-delay-500 overflow-hidden relative group bg-turquoise-900/90 border-turquoise-700">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-turquoise-200 mb-1">Waiver</p>
                <p className="text-lg font-viking text-white">Loading...</p>
              </div>
              <div className="p-3 bg-turquoise-500/20 rounded-xl">
                <Loader2 className="h-6 w-6 text-turquoise-300 animate-spin" />
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (waiverDisplayStatus === 'signed') {
      return (
        <Card className="hover-lift animate-on-load animate-fade-in-up animation-delay-500 overflow-hidden relative group bg-turquoise-900/90 border-turquoise-700">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-turquoise-200 mb-1">Waiver</p>
                <p className="text-lg font-viking text-green-400">Signed</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-xl">
                <CheckCircle2 className="h-6 w-6 text-green-400" />
              </div>
            </div>
            <p className="text-xs text-turquoise-300 mt-2">
              {waiverStatus?.signedAt
                ? `Signed ${new Date(waiverStatus.signedAt).toLocaleDateString()}`
                : 'Completed'}
            </p>
          </CardContent>
        </Card>
      );
    }

    if (waiverDisplayStatus === 'pending') {
      return (
        <Card
          className="hover-lift animate-on-load animate-fade-in-up animation-delay-500 overflow-hidden relative group cursor-pointer bg-turquoise-900/90 border-turquoise-700"
          onClick={() => setShowWaiverModal(true)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-turquoise-200 mb-1">Waiver</p>
                <p className="text-lg font-viking text-yellow-400">Pending</p>
              </div>
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
            </div>
            <p className="text-xs text-orange-400 mt-2 font-medium group-hover:underline">
              Complete signing →
            </p>
          </CardContent>
        </Card>
      );
    }

    // not_signed or needs_action
    return (
      <Card
        className="hover-lift animate-on-load animate-fade-in-up animation-delay-500 overflow-hidden relative group cursor-pointer bg-turquoise-900/90 border-turquoise-700"
        onClick={() => setShowWaiverModal(true)}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-turquoise-200 mb-1">Waiver</p>
              <p className="text-lg font-viking text-orange-400">
                {waiverDisplayStatus === 'needs_action' ? 'Action Required' : 'Not Signed'}
              </p>
            </div>
            <div className="p-3 bg-orange-500/20 rounded-xl">
              <FileSignature className="h-6 w-6 text-orange-400" />
            </div>
          </div>
          <p className="text-xs text-orange-400 mt-2 font-medium group-hover:underline">
            Sign waiver →
          </p>
        </CardContent>
      </Card>
    );
  };

  // Stats Dashboard for users with registrations
  const RegisteredUserDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Tournaments Registered */}
        <Card className="hover-lift animate-on-load animate-fade-in-up animation-delay-100 overflow-hidden relative group bg-turquoise-900/90 border-turquoise-700">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-turquoise-200 mb-1">Tournaments</p>
                <p className="text-4xl font-viking text-white animate-on-load animate-count-up animation-delay-300">
                  {registrations.tournaments.length}
                </p>
              </div>
              <div className="p-3 bg-orange-500/20 rounded-xl">
                <Trophy className="h-6 w-6 text-orange-400" />
              </div>
            </div>
            <p className="text-xs text-turquoise-300 mt-2">Registered events</p>
          </CardContent>
        </Card>

        {/* Profile */}
        <Card
          className="hover-lift animate-on-load animate-fade-in-up animation-delay-400 overflow-hidden relative group cursor-pointer bg-turquoise-900/90 border-turquoise-700"
          onClick={() => navigate('/profile')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-turquoise-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-turquoise-200 mb-1">Profile</p>
                <p className="text-lg font-viking text-white">Active</p>
              </div>
              <div className="p-3 bg-turquoise-500/20 rounded-xl">
                <User className="h-6 w-6 text-turquoise-300" />
              </div>
            </div>
            <p className="text-xs text-orange-400 mt-2 font-medium group-hover:underline">
              View profile →
            </p>
          </CardContent>
        </Card>

        {/* Add-ons Purchased */}
        {purchasedAddons.length > 0 && (
          <Card className="hover-lift animate-on-load animate-fade-in-up animation-delay-300 overflow-hidden relative group bg-turquoise-900/90 border-turquoise-700">
            <div className="absolute inset-0 bg-gradient-to-br from-turquoise-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-turquoise-200 mb-1">Add-ons</p>
                  <p className="text-4xl font-viking text-white animate-on-load animate-count-up animation-delay-400">
                    {purchasedAddons.length}
                  </p>
                </div>
                <div className="p-3 bg-turquoise-500/20 rounded-xl">
                  <ShoppingBag className="h-6 w-6 text-turquoise-300" />
                </div>
              </div>
              <p className="text-xs text-turquoise-300 mt-2">Items purchased</p>
            </CardContent>
          </Card>
        )}

        {/* Day Passes */}
        {ownedDayPassIds.length > 0 && (
          <Card className="animate-on-load animate-fade-in-up bg-turquoise-900/90 border-turquoise-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-turquoise-200">Day Passes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {ownedDayPassIds.map((id) => (
                  <span
                    key={id}
                    className="px-3 py-1 rounded-full text-xs font-bold bg-orange-500/20 text-orange-300 border border-orange-500/40"
                  >
                    {dayPassName(id)}
                  </span>
                ))}
              </div>
              <p className="text-xs text-turquoise-300 mt-2">Single-day event access</p>
            </CardContent>
          </Card>
        )}

        {/* Waiver Status - only show if user has an event registration */}
        {hasEventRegistration && <WaiverStatusCard />}
      </div>

      {/* Your Tournaments */}
      <Card className="animate-on-load animate-fade-in-up animation-delay-500 bg-turquoise-900/90 border-turquoise-700">
        <CardHeader className="border-b border-turquoise-700">
          <div className="flex items-center justify-between">
            <CardTitle className="font-viking text-xl text-white">Your Tournaments</CardTitle>
            {!REGISTRATIONS_CLOSED && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/event-registration')}
                className="text-orange-400 border-orange-500/50 hover:bg-orange-500/20"
              >
                <Trophy className="mr-2 h-4 w-4" />
                Register More
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-turquoise-700">
            {registrations.tournaments.map((reg, index) => (
              <div
                key={reg.id}
                className={`p-4 hover:bg-turquoise-800/50 transition-colors animate-on-load animate-slide-in-right`}
                style={{ animationDelay: `${600 + index * 100}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <Swords className="h-5 w-5 text-orange-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{reg.tournament.name}</h4>
                      <p className="text-sm text-turquoise-300">
                        Registered {new Date(reg.registeredAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      reg.paymentStatus === 'completed'
                        ? 'bg-green-500/20 text-green-300'
                        : 'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {reg.paymentStatus === 'completed' ? 'Confirmed' : 'Pending'}
                    </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Your Add-ons */}
      {purchasedAddons.length > 0 && (
        <Card className="animate-on-load animate-fade-in-up animation-delay-550 bg-turquoise-900/90 border-turquoise-700">
          <CardHeader className="border-b border-turquoise-700">
            <div className="flex items-center justify-between">
              <CardTitle className="font-viking text-xl text-white">Your Add-ons</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/orders')}
                className="text-orange-400 border-orange-500/50 hover:bg-orange-500/20"
              >
                <ShoppingBag className="mr-2 h-4 w-4" />
                View Orders
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-turquoise-700">
              {purchasedAddons.map((addon, index) => (
                <div
                  key={addon.id}
                  className={`p-4 hover:bg-turquoise-800/50 transition-colors animate-on-load animate-slide-in-right`}
                  style={{ animationDelay: `${650 + index * 100}ms` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {addon.imageUrl ? (
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-turquoise-800">
                          <img
                            src={addon.imageUrl}
                            alt={addon.itemName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="p-2 bg-turquoise-500/20 rounded-lg">
                          <Package className="h-5 w-5 text-turquoise-300" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-medium text-white">
                          {addon.itemName}
                          {addon.variantName && (
                            <span className="text-turquoise-300 font-normal"> - {addon.variantName}</span>
                          )}
                        </h4>
                        <p className="text-sm text-turquoise-300">
                          Qty: {addon.quantity} • Purchased {new Date(addon.purchasedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300">
                      ${(addon.price / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="hover-lift animate-on-load animate-fade-in-up animation-delay-600 bg-turquoise-900/90 border-turquoise-700">
          <CardContent className="p-6">
            <h3 className="font-viking text-lg text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {!REGISTRATIONS_CLOSED && (
                <Button
                  className="w-full justify-between bg-orange-500 hover:bg-orange-600 text-white group"
                  onClick={() => navigate('/event-registration')}
                >
                  <span className="flex items-center">
                    <Trophy className="mr-2 h-4 w-4" />
                    Register for More
                  </span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full justify-between border-turquoise-600 text-turquoise-200 hover:bg-turquoise-800 group"
                onClick={() => navigate('/orders')}
              >
                <span className="flex items-center">
                  <Clock className="mr-2 h-4 w-4" />
                  Order History
                </span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                className="w-full justify-between border-turquoise-600 text-turquoise-200 hover:bg-turquoise-800 group"
                onClick={() => navigate('/profile')}
              >
                <span className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  Edit Profile
                </span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift animate-on-load animate-fade-in-up animation-delay-700 bg-turquoise-900/90 border-turquoise-700">
          <CardContent className="p-6">
            <h3 className="font-viking text-lg text-white mb-4">Event Details</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-turquoise-500/20 rounded-lg">
                  <CalendarDays className="h-5 w-5 text-turquoise-300" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Dates</p>
                  <p className="text-sm text-turquoise-300">{eventDate}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-turquoise-500/20 rounded-lg">
                  <MapPin className="h-5 w-5 text-turquoise-300" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Location</p>
                  <p className="text-sm text-turquoise-300">{eventVenue}</p>
                </div>
              </div>
              <Button
                variant="link"
                className="px-0 text-orange-400 hover:text-orange-300"
                onClick={() => navigate('/about')}
              >
                View full event details →
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Create a general event calendar invite
                  const icsContent = generateCactusCupTournamentInvite(
                    'Cactus Cup 2026',
                    '2026-06-14'
                  );
                  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'cactus-cup-2026.ics';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
                className="mt-2 w-full border-turquoise-600 text-turquoise-200 hover:bg-turquoise-800"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Add to Calendar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-900/40 text-green-300 p-4 rounded-xl border border-green-700/50 animate-fade-in-down flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-on-load animate-fade-in-down">
            <div>
              <h1 className="text-3xl md:text-4xl font-viking text-white mb-2">
                {hasRegistrations ? `Welcome Back, ${userName}!` : 'Your Dashboard'}
              </h1>
              <p className="text-white/70">
                {hasRegistrations
                  ? 'Here\'s your tournament overview'
                  : `Welcome, ${userName}! Ready to join the action?`
                }
              </p>
            </div>
            <Button
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 self-start sm:self-auto"
              onClick={handleSignOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>

          {/* Main Content - Conditional based on registrations */}
          {hasRegistrations ? <RegisteredUserDashboard /> : <NoRegistrationsHero />}
        </div>
      </div>

      {/* Waiver Signing Modal */}
      {showWaiverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              if (!signUrl) {
                setShowWaiverModal(false);
                setLocalWaiverError(null);
              }
            }}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto bg-turquoise-800 rounded-2xl border-2 border-orange-500 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-turquoise-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <FileSignature className="h-6 w-6 text-orange-400" />
                </div>
                <div>
                  <h2 className="font-viking text-xl text-white">
                    {signUrl ? 'Sign Your Waiver' : waiverSettings.waiverTitle}
                  </h2>
                  {!signUrl && (
                    <p className="text-sm text-turquoise-300">Complete your waiver to participate</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowWaiverModal(false);
                  setSignUrl(null);
                  setLocalWaiverError(null);
                }}
                className="p-2 hover:bg-turquoise-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-turquoise-300" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {signUrl ? (
                /* BoldSign iframe */
                <div className="space-y-4">
                  <p className="text-sm text-turquoise-300">
                    Please sign the waiver below. This page will automatically update when complete.
                  </p>
                  <div className="rounded-lg overflow-hidden border border-turquoise-600">
                    <iframe
                      src={signUrl}
                      width="100%"
                      height="500"
                      allow="camera"
                      title="BoldSign Waiver Signing"
                      className="bg-white border-0"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => {
                        setSignUrl(null);
                      }}
                      className="text-turquoise-300 hover:text-turquoise-200"
                    >
                      ← Back to form
                    </button>
                    <a
                      href={signUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-400 hover:text-orange-300 flex items-center gap-1"
                    >
                      Open in new tab <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ) : (
                /* Signing form */
                <div className="space-y-6">
                  <p className="text-turquoise-200">{waiverSettings.waiverDescription}</p>

                  {(waiverError || localWaiverError) && (
                    <div className="bg-red-500/20 border border-red-400/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-red-300">
                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                        <span>{waiverError || localWaiverError}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="modal-signerName" className="text-orange-100">
                        Full Legal Name <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="modal-signerName"
                        value={signerName}
                        onChange={(e) => {
                          setSignerName(e.target.value);
                          setLocalWaiverError(null);
                        }}
                        placeholder="Enter your full legal name"
                        className="mt-1 bg-turquoise-700 border-turquoise-500 text-white placeholder:text-turquoise-400"
                        required
                      />
                      <p className="text-xs text-turquoise-400 mt-1">
                        This name will appear on your signed waiver
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="modal-signerEmail" className="text-orange-100">
                        Email Address <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        id="modal-signerEmail"
                        type="email"
                        value={signerEmail}
                        onChange={(e) => {
                          setSignerEmail(e.target.value);
                          setLocalWaiverError(null);
                        }}
                        placeholder="your@email.com"
                        className="mt-1 bg-turquoise-700 border-turquoise-500 text-white placeholder:text-turquoise-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-turquoise-700">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowWaiverModal(false);
                        setLocalWaiverError(null);
                      }}
                      className="border-turquoise-600 text-turquoise-200 hover:bg-turquoise-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateWaiver}
                      disabled={isCreatingWaiver || !signerName.trim() || !signerEmail.trim()}
                      className="bg-orange-500 hover:bg-orange-600 text-white min-w-[180px]"
                    >
                      {isCreatingWaiver ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Preparing...
                        </>
                      ) : (
                        <>
                          <FileSignature className="h-4 w-4 mr-2" />
                          Continue to Sign
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
